import { Disclosure } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { ArrowUpRight, CheckCircle2, Loader2, PhoneIcon } from 'lucide-react';
import { useMemo } from 'react';

import { formatPhoneNumber } from '@/components/audio-conversation/utils';
import type {
  EmailSubmissionChannel,
  FaxSubmissionChannel,
  MailingAddressSubmissionChannel,
  PortalSubmissionChannel,
  SubmissionMethod,
  WebsiteSubmissionChannel,
} from '@/services/api/generated/taskActivityService';
import type { TreatmentRecordRequestChannelSchema } from '@/services/api/generated/treatmentRecordRequestsService';
import type {
  ContactSubmissionMethod,
  ExtendedTreatmentRecordRequestMetadata,
} from '@/types/treatment-record-request-extended';
import type { AlternateContact } from '@/types/treatment-request-alternates';
import { canonicalizeHandleType } from '@/utils/treatment-request-alternates';

type SubmissionChannel = SubmissionMethod['submission_method'];

interface NormalizedSubmissionMethod {
  key: string;
  label: string;
  description?: string;
  valueDisplay?: string;
  rawValue?: string | null;
  isPreferred?: boolean;
  canApply: boolean;
  raw?: SubmissionMethod;
  isActive?: boolean;
}

interface NormalizedAlternate extends AlternateContact {
  key: string;
}

interface ContactCardItem {
  key: string;
  title: string;
  secondary?: string;
  value: string;
  description?: string;
  confidence?: number | null;
  onApply?: (() => void) | null;
  isPending?: boolean;
  actionLabel?: string;
}

const formatTimestamp = (value?: string | null) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString();
};

const isStructuredSubmissionMethod = (
  method: ContactSubmissionMethod,
): method is SubmissionMethod =>
  typeof method === 'object' &&
  method !== null &&
  'submission_method' in method;

const formatMailingAddress = (
  handle?: MailingAddressSubmissionChannel['handle'] | null,
) => {
  if (!handle) return null;
  const locality = [handle.city, handle.state].filter(Boolean).join(', ');
  const cityLine = [locality, handle.zip_code].filter(Boolean).join(' ');
  return [
    handle.recipient,
    handle.address_line_1,
    handle.address_line_2,
    cityLine,
    handle.country,
  ]
    .filter((line) => Boolean(line && line.trim()))
    .join('\n');
};

const getChannelTypeFromSubmission = (submission?: SubmissionChannel) => {
  if (!submission) return '';
  if ('channel_type' in submission && submission.channel_type) {
    return submission.channel_type.toUpperCase();
  }
  if ('handle' in submission && submission.handle) {
    const handle = submission.handle as { handle_type?: string | null };
    return handle?.handle_type?.toUpperCase() || '';
  }
  return '';
};

const extractHandleValue = (
  channelType: string,
  submission?: SubmissionChannel,
): { display?: string; raw?: string | null } => {
  if (!submission) return { raw: null };
  switch (channelType) {
    case 'FAX':
    case 'PHONE': {
      const raw = (submission as FaxSubmissionChannel)?.handle?.value || null;
      return {
        raw,
        display: raw ? formatPhoneNumber(raw) || raw : undefined,
      };
    }
    case 'EMAIL': {
      const raw = (submission as EmailSubmissionChannel)?.handle?.value || null;
      return { raw, display: raw || undefined };
    }
    case 'MAILING_ADDRESS': {
      const raw = formatMailingAddress(
        (submission as MailingAddressSubmissionChannel)?.handle,
      );
      return { raw, display: raw || undefined };
    }
    case 'PORTAL': {
      const raw = (submission as PortalSubmissionChannel)?.portal_id || null;
      return {
        raw,
        display: raw ? `Portal ID ${raw}` : undefined,
      };
    }
    case 'WEBSITE': {
      const website =
        (submission as WebsiteSubmissionChannel)?.website ||
        (submission as WebsiteSubmissionChannel)?.handle ||
        null;
      return { raw: website, display: website || undefined };
    }
    default:
      return { raw: null };
  }
};

const normalizeValueForKey = (channelType: string, value?: string | null) => {
  if (!value) return '';
  const trimmed = value.replace(/\s+/g, ' ').trim();
  if (channelType === 'FAX' || channelType === 'PHONE') {
    return trimmed.replace(/[^\d+]/g, '');
  }
  if (channelType === 'EMAIL') {
    return trimmed.toLowerCase();
  }
  if (channelType === 'MAILING_ADDRESS') {
    return trimmed.toLowerCase();
  }
  return trimmed.toLowerCase();
};

const buildSubmissionKey = (
  channelType: string,
  value?: string | null,
  fallback = 'unknown',
) => {
  const normalizedType = channelType || 'UNKNOWN';
  const normalizedValue =
    normalizeValueForKey(normalizedType, value) || fallback;
  return `${normalizedType}::${normalizedValue}`;
};

const normalizeAlternateForKey = (
  alternate: AlternateContact,
): NormalizedAlternate => {
  const canonicalType = canonicalizeHandleType(alternate.handle_type);
  const key = buildSubmissionKey(
    canonicalType,
    alternate.value,
    alternate.value,
  );
  return {
    ...alternate,
    handle_type: canonicalType,
    key,
  };
};

const getActiveChannelKey = (
  channel?: TreatmentRecordRequestChannelSchema | null,
) => {
  if (!channel) return null;
  switch (channel.type) {
    case 'fax':
      return buildSubmissionKey(
        'FAX',
        channel.fax || channel.phone_number,
        'fax',
      );
    case 'email':
      return buildSubmissionKey('EMAIL', channel.email, 'email');
    case 'mail':
      return buildSubmissionKey('MAILING_ADDRESS', channel.address, 'mail');
    case 'portal':
      return buildSubmissionKey('PORTAL', channel.portal_provider_id, 'portal');
    case 'phone':
      return buildSubmissionKey('PHONE', channel.phone_number, 'phone');
    default:
      return null;
  }
};

const normalizeSubmissionMethods = (
  methods: ContactSubmissionMethod[] | null | undefined,
  activeKey: string | null,
): NormalizedSubmissionMethod[] => {
  if (!methods) return [];
  const normalized: NormalizedSubmissionMethod[] = [];

  methods.forEach((method, index) => {
    if (!isStructuredSubmissionMethod(method)) {
      const label = typeof method === 'string' ? method.trim() : '';
      if (!label) return;
      const key = `text-${index}-${label.toLowerCase()}`;
      normalized.push({
        key,
        label: label.toUpperCase(),
        canApply: false,
      });
      return;
    }

    const submissionChannel = method.submission_method;
    const channelType =
      getChannelTypeFromSubmission(submissionChannel) || 'UNKNOWN';
    const { display, raw } = extractHandleValue(channelType, submissionChannel);
    const fallback = `structured-${index}`;
    const key = buildSubmissionKey(channelType, raw, fallback);
    normalized.push({
      key,
      label: channelType.replace(/_/g, ' ').toUpperCase(),
      description: method.submission_instructions || method.reason || undefined,
      valueDisplay: display,
      rawValue: raw ?? null,
      isPreferred: submissionChannel?.is_preferred,
      canApply: Boolean(submissionChannel),
      raw: method,
      isActive: key === activeKey,
    });
  });

  return normalized;
};

const normalizeAlternate = (alternate: unknown): AlternateContact | null => {
  if (!alternate || typeof alternate !== 'object') return null;
  const record = alternate as Record<string, unknown>;
  const rawValue = record.value;
  const value =
    typeof rawValue === 'string'
      ? rawValue.trim()
      : rawValue !== undefined && rawValue !== null
        ? String(rawValue).trim()
        : '';
  if (!value) return null;

  const rawHandle = record.handle_type || record.type;
  const handleType =
    typeof rawHandle === 'string' && rawHandle.trim()
      ? rawHandle.trim().toUpperCase()
      : 'PHONE';

  const rawLabel = record.label;
  const label =
    typeof rawLabel === 'string'
      ? rawLabel.trim()
      : rawLabel !== undefined && rawLabel !== null
        ? String(rawLabel).trim()
        : null;

  const confidence =
    typeof record.confidence === 'number'
      ? Math.min(Math.max(record.confidence, 0), 1)
      : null;

  return {
    handle_type: handleType as AlternateContact['handle_type'],
    value,
    label: label || null,
    confidence,
  };
};

const formatHandleTypeLabel = (value?: string | null) => {
  if (!value) return 'Alternate Contact';
  const upper = value.toUpperCase();
  switch (upper) {
    case 'FAX':
      return 'Fax';
    case 'PHONE':
      return 'Phone';
    case 'EMAIL':
      return 'Email';
    case 'MAILING_ADDRESS':
      return 'Mailing Address';
    default:
      return value
        .toLowerCase()
        .split('_')
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ');
  }
};

const formatDialTarget = (value?: string | null) => {
  if (!value) return '';
  const trimmed = value.trim();
  if (!trimmed) return '';
  if (trimmed.startsWith('+1')) {
    const withoutCountry = trimmed.slice(2).trim();
    const formatted = formatPhoneNumber(withoutCountry || trimmed);
    return `+1 ${formatted || withoutCountry}`.trim();
  }
  if (trimmed.startsWith('+')) {
    return trimmed;
  }
  return formatPhoneNumber(trimmed) || trimmed;
};

interface ContactDiscoveryPanelProps {
  metadata?: ExtendedTreatmentRecordRequestMetadata | null;
  channelPhoneNumber?: string | null;
  targetLabel?: string | null;
  channel?: TreatmentRecordRequestChannelSchema | null;
  onSubmissionMethodSelect?: (method: SubmissionMethod, key: string) => void;
  onAlternateSelect?: (alternate: AlternateContact, key: string) => void;
  pendingActionKey?: string | null;
}

export const ContactDiscoveryPanel = ({
  metadata,
  channelPhoneNumber,
  targetLabel,
  channel,
  onSubmissionMethodSelect,
  onAlternateSelect,
  pendingActionKey,
}: ContactDiscoveryPanelProps) => {
  const summary = metadata?.contact_discovery_summary;
  const callSummary =
    summary?.call_summary?.trim() ||
    metadata?.voice_ai_call_summary?.trim() ||
    null;
  const lastUpdated = formatTimestamp(
    metadata?.contact_discovery_last_updated_at,
  );
  const notes = summary?.notes?.trim() || null;
  const activeSubmissionKey = useMemo(
    () => getActiveChannelKey(channel),
    [channel],
  );

  const submissionMethods = useMemo(
    () =>
      normalizeSubmissionMethods(
        summary?.submission_methods,
        activeSubmissionKey,
      ),
    [summary?.submission_methods, activeSubmissionKey],
  );

  const requiredDocuments = useMemo(() => {
    if (!summary?.required_documents) return [];
    return summary.required_documents
      .map((doc) => doc?.trim())
      .filter((doc): doc is string => Boolean(doc));
  }, [summary?.required_documents]);

  const alternateContactsRaw = useMemo(() => {
    const structured = (summary?.alternate_contacts || [])
      .map((alternate) => normalizeAlternate(alternate))
      .filter((alt): alt is AlternateContact => Boolean(alt));

    if (structured.length > 0) {
      return structured;
    }

    return (metadata?.voice_ai_discovered_alternates || [])
      .map((alternate) => normalizeAlternate(alternate))
      .filter((alt): alt is AlternateContact => Boolean(alt));
  }, [metadata?.voice_ai_discovered_alternates, summary?.alternate_contacts]);

  const submissionKeys = useMemo(
    () => new Set(submissionMethods.map((method) => method.key)),
    [submissionMethods],
  );

  const alternateContacts = useMemo(
    () =>
      alternateContactsRaw
        .map((alternate) => normalizeAlternateForKey(alternate))
        .filter((alternate) => !submissionKeys.has(alternate.key)),
    [alternateContactsRaw, submissionKeys],
  );

  const primaryContactCard = useMemo<ContactCardItem | null>(() => {
    if (!submissionMethods.length) return null;
    const structuredMethod = submissionMethods.find(
      (method) => method.raw && (method.valueDisplay || method.rawValue),
    );
    if (!structuredMethod) return null;
    const value = structuredMethod.valueDisplay || structuredMethod.rawValue;
    if (!value) return null;

    const canApply = Boolean(
      onSubmissionMethodSelect &&
        structuredMethod.canApply &&
        structuredMethod.raw,
    );

    const action = canApply
      ? () =>
          structuredMethod.raw &&
          onSubmissionMethodSelect?.(structuredMethod.raw, structuredMethod.key)
      : undefined;

    return {
      key: `primary-${structuredMethod.key}`,
      title: 'Auto-selected Contact',
      secondary: structuredMethod.label,
      value,
      description: structuredMethod.description,
      onApply: action,
      isPending: pendingActionKey === structuredMethod.key,
      actionLabel: 'Apply Method',
    };
  }, [submissionMethods, onSubmissionMethodSelect, pendingActionKey]);

  const contactCards = useMemo<ContactCardItem[]>(() => {
    const cards: ContactCardItem[] = [];
    if (primaryContactCard) {
      cards.push(primaryContactCard);
    }

    alternateContacts.forEach((alternate) => {
      cards.push({
        key: alternate.key,
        title: formatHandleTypeLabel(alternate.handle_type),
        value: alternate.value,
        description: alternate.label ?? undefined,
        confidence: alternate.confidence,
        onApply: onAlternateSelect
          ? () => onAlternateSelect(alternate, alternate.key)
          : null,
        isPending: pendingActionKey === alternate.key,
        actionLabel: 'Use for Request',
      });
    });

    return cards;
  }, [
    alternateContacts,
    onAlternateSelect,
    pendingActionKey,
    primaryContactCard,
  ]);

  const hasContacts = contactCards.length > 0;

  const panelHasContent = Boolean(
    callSummary || requiredDocuments.length || hasContacts || notes,
  );

  const workflowComplete = Boolean(
    metadata?.initial_contact_method_call_completed_at,
  );
  const shouldShowSkeleton = workflowComplete && !panelHasContent;

  if (!channelPhoneNumber && !shouldShowSkeleton && !panelHasContent) {
    return null;
  }

  return (
    <div className="rounded-2xl border border-blue-100 bg-white shadow-sm">
      <div className="flex flex-wrap items-center gap-4 bg-blue-50 px-5 py-4 text-blue-900">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-white text-blue-600">
            <PhoneIcon className="h-4 w-4" />
          </div>
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wide text-blue-700">
              Requesting Submission Method for Records/Bills
            </p>
            <p className="text-sm font-medium">
              {targetLabel?.trim() || 'Medical Records'}
            </p>
          </div>
        </div>

        {channelPhoneNumber && (
          <div className="ml-auto text-right">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-blue-700">
              Phone
            </p>
            <p className="text-sm font-semibold text-blue-900 font-mono">
              {formatDialTarget(channelPhoneNumber)}
            </p>
          </div>
        )}

        {callSummary && (
          <div className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-blue-700">
            <CheckCircle2 className="h-4 w-4" />
            <span>
              Voice AI Call Complete
              {lastUpdated ? ` • ${lastUpdated}` : ''}
            </span>
          </div>
        )}
      </div>

      <div className="border-t border-blue-100 px-5 py-5">
        {shouldShowSkeleton && (
          <div data-testid="contact-discovery-skeleton" className="space-y-3">
            <div className="h-3 w-2/5 rounded bg-zinc-100 animate-pulse" />
            <div className="h-4 w-full rounded bg-zinc-100 animate-pulse" />
            <div className="h-4 w-5/6 rounded bg-zinc-100 animate-pulse" />
            <div className="grid gap-2 sm:grid-cols-2">
              <div className="h-16 rounded bg-zinc-100 animate-pulse" />
              <div className="h-16 rounded bg-zinc-100 animate-pulse" />
            </div>
          </div>
        )}

        {!shouldShowSkeleton && panelHasContent && (
          <div className="space-y-5">
            {callSummary && (
              <div className="space-y-1">
                <p className="text-xs font-semibold uppercase tracking-wide text-zinc-500">
                  Voice AI Summary
                </p>
                <p className="text-sm leading-relaxed text-zinc-800 whitespace-pre-line">
                  {callSummary}
                </p>
                {lastUpdated && (
                  <p className="text-xs text-zinc-500">
                    Last updated {lastUpdated}
                  </p>
                )}
              </div>
            )}

            {requiredDocuments.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-zinc-500">
                  Required Documents
                </p>
                <ul className="list-disc space-y-1 pl-5 text-sm text-zinc-800">
                  {requiredDocuments.map((doc) => (
                    <li key={doc}>{doc}</li>
                  ))}
                </ul>
              </div>
            )}

            {contactCards.length > 0 && (
              <div className="space-y-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-zinc-500">
                  Contacts
                </p>
                <div className="space-y-2">
                  {contactCards.map((card) => (
                    <div
                      key={card.key}
                      className={`space-y-2 rounded-xl border border-zinc-200 bg-zinc-50 p-3 ${
                        card.onApply ? 'hover:border-blue-200 transition' : ''
                      }`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-zinc-500">
                          {card.title}
                        </p>
                        {card.secondary && (
                          <span className="text-[10px] font-semibold uppercase tracking-wide text-zinc-400">
                            {card.secondary}
                          </span>
                        )}
                      </div>
                      <p className="font-mono text-sm text-zinc-900 break-all">
                        {card.value}
                      </p>
                      {card.description && (
                        <p className="text-xs text-zinc-500">
                          {card.description}
                        </p>
                      )}
                      {typeof card.confidence === 'number' && (
                        <p className="text-xs text-zinc-500">
                          Confidence {Math.round(card.confidence * 100)}%
                        </p>
                      )}
                      {card.onApply && card.actionLabel && (
                        <button
                          type="button"
                          className={`inline-flex items-center gap-1 text-xs font-semibold text-blue-600 ${
                            card.isPending
                              ? 'opacity-70'
                              : 'hover:text-blue-800'
                          }`}
                          onClick={card.onApply}
                          disabled={card.isPending}
                        >
                          {card.isPending ? (
                            <>
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              <span>Applying…</span>
                            </>
                          ) : (
                            <>
                              <span>{card.actionLabel}</span>
                              <ArrowUpRight className="h-3.5 w-3.5" />
                            </>
                          )}
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {notes && (
              <Disclosure>
                {({ open }) => (
                  <div className="rounded-xl border border-zinc-100 bg-zinc-50">
                    <Disclosure.Button className="flex w-full items-center justify-between px-3 py-2 text-sm font-medium text-zinc-700">
                      <span>Notes</span>
                      <ChevronDownIcon
                        className={`h-4 w-4 transition-transform ${open ? 'rotate-180' : ''}`}
                      />
                    </Disclosure.Button>
                    <Disclosure.Panel className="px-3 pb-3 text-sm leading-relaxed text-zinc-700 whitespace-pre-line">
                      {notes}
                    </Disclosure.Panel>
                  </div>
                )}
              </Disclosure>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
